import React, { useState, useEffect } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import SyncIcon from '@mui/icons-material/Sync'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { copilotSourcesEndpoint } from './variables'

const actionIconProps = { sx: { fontSize: 18, color: '#12332a' } }
const closeIconProps = { sx: { fontSize: 18, color: '#567469' } }

export default function DataSourceManager({ organizationId, onClose }) {
  const [sources, setSources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  const [newUrl, setNewUrl] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newType, setNewType] = useState('website')
  const [adding, setAdding] = useState(false)

  const fetchSources = async () => {
    if (!organizationId) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const res = await fetch(`${copilotSourcesEndpoint}/${organizationId}`)
      if (!res.ok) throw new Error('Failed to load sources')
      const data = await res.json()
      setSources(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSources()
    
    // Poll every 5 seconds if any source is currently scraping
    const interval = setInterval(() => {
      setSources(current => {
        if (current.some(s => s.status === 'scraping')) {
          fetchSources()
        }
        return current
      })
    }, 5000)
    
    return () => clearInterval(interval)
  }, [organizationId])

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!newUrl || !organizationId) return
    
    setAdding(true)
    setError(null)
    
    try {
      const res = await fetch(copilotSourcesEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          url: newUrl,
          label: newLabel || newUrl,
          type: newType
        })
      })
      
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Failed to add source')
      }
      
      setNewUrl('')
      setNewLabel('')
      setNewType('website')
      await fetchSources()
    } catch (err) {
      setError(err.message)
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (sourceId) => {
    try {
      const res = await fetch(`${copilotSourcesEndpoint}/${organizationId}/${sourceId}`, {
        method: 'DELETE'
      })
      if (!res.ok) throw new Error('Failed to delete')
      setSources(sources.filter(s => s.id !== sourceId))
    } catch (err) {
      setError(err.message)
    }
  }

  const handleSync = async (sourceId) => {
    try {
      const res = await fetch(`${copilotSourcesEndpoint}/${organizationId}/${sourceId}/sync`, {
        method: 'POST'
      })
      if (!res.ok) throw new Error('Failed to sync')
      
      // Update local state to show scraping
      setSources(sources.map(s => 
        s.id === sourceId ? { ...s, status: 'scraping', error: null } : s
      ))
    } catch (err) {
      setError(err.message)
    }
  }

  const handleSyncAll = async () => {
    try {
      const res = await fetch(`${copilotSourcesEndpoint}/${organizationId}/sync-all`, {
        method: 'POST'
      })
      if (!res.ok) throw new Error('Failed to sync all')
      
      setSources(sources.map(s => ({ ...s, status: 'scraping', error: null })))
    } catch (err) {
      setError(err.message)
    }
  }

  if (!organizationId) {
    return (
      <div className="ds-manager ds-empty">
        <div className="ds-header">
          <h3>Knowledge Base</h3>
          <button onClick={onClose} className="ds-close" aria-label="Close settings">
            <CloseIcon {...closeIconProps} />
          </button>
        </div>
        <p>Please log in to manage your organization's knowledge base.</p>
      </div>
    )
  }

  return (
    <div className="ds-manager">
      <div className="ds-header">
        <h3>Knowledge Base Config</h3>
        <button onClick={onClose} className="ds-close" aria-label="Close settings">
          <CloseIcon {...closeIconProps} />
        </button>
      </div>
      
      {error && <div className="ds-error">{error}</div>}
      
      <form onSubmit={handleAdd} className="ds-add-form">
        <div className="ds-form-row">
          <input 
            type="url" 
            placeholder="URL (e.g. https://company.com)" 
            value={newUrl} 
            onChange={e => setNewUrl(e.target.value)}
            required
            disabled={adding}
          />
        </div>
        <div className="ds-form-row ds-flex-row">
          <input 
            type="text" 
            placeholder="Label (optional)" 
            value={newLabel} 
            onChange={e => setNewLabel(e.target.value)}
            disabled={adding}
          />
          <select value={newType} onChange={e => setNewType(e.target.value)} disabled={adding}>
            <option value="website">Website</option>
            <option value="social_media">Social Media</option>
            <option value="wiki">Wiki / Docs</option>
            <option value="custom">Other</option>
          </select>
          <button type="submit" disabled={adding || !newUrl} className="ds-add-btn">
            {adding ? '...' : 'Add'}
          </button>
        </div>
      </form>
      
      <div className="ds-list-header">
        <h4>Configured Sources ({sources.length})</h4>
        {sources.length > 0 && (
          <button onClick={handleSyncAll} className="ds-sync-all">Sync All</button>
        )}
      </div>
      
      <div className="ds-list">
        {loading && sources.length === 0 ? (
          <div className="ds-empty">Loading sources...</div>
        ) : sources.length === 0 ? (
          <div className="ds-empty">No sources configured yet. Add a website URL above.</div>
        ) : (
          sources.map(source => (
            <div key={source.id} className="ds-item">
              <div className="ds-item-main">
                <div className="ds-item-title">
                  <span className={`ds-status ds-status-${source.status}`} title={source.status}></span>
                  <span className="ds-label" title={source.url}>{source.label}</span>
                </div>
                <div className="ds-item-meta">
                  {source.type} • {source.status === 'scraping' ? 'Syncing...' : source.lastSyncedAt ? new Date(source.lastSyncedAt).toLocaleString() : 'Never synced'}
                </div>
                {source.error && <div className="ds-item-error">{source.error}</div>}
              </div>
              <div className="ds-item-actions">
                <button onClick={() => handleSync(source.id)} disabled={source.status === 'scraping'} title="Sync now" aria-label="Sync source">
                  <SyncIcon {...actionIconProps} />
                </button>
                <button onClick={() => handleDelete(source.id)} className="ds-delete" title="Delete" aria-label="Delete source">
                  <DeleteOutlineIcon sx={{ fontSize: 18, color: '#d85a5a' }} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <style>{`
        .ds-manager {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #fdfdfd;
          font-family: inherit;
        }
        .ds-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 14px;
          background: linear-gradient(90deg,#f0fff4,#eaf9ee);
          border-bottom: 1px solid rgba(18,51,42,0.06);
        }
        .ds-header h3 {
          margin: 0;
          font-size: 14px;
          color: #12332a;
        }
        .ds-close {
          background: transparent;
          border: none;
          color: #12332a;
          cursor: pointer;
          font-size: 12px;
          padding: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .ds-close:hover { background: rgba(0,0,0,0.05); border-radius: 4px; }
        .ds-error {
          padding: 8px 12px;
          background: #fee;
          color: #c00;
          font-size: 12px;
          border-bottom: 1px solid #fcc;
        }
        .ds-add-form {
          padding: 12px;
          border-bottom: 1px solid #eee;
          display: flex;
          flex-direction: column;
          gap: 8px;
          background: #fafafa;
        }
        .ds-form-row { display: flex; width: 100%; }
        .ds-flex-row { gap: 6px; }
        .ds-form-row input, .ds-form-row select {
          padding: 6px 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: inherit;
        }
        .ds-form-row input[type="url"] { width: 100%; }
        .ds-form-row input[type="text"] { flex: 1; min-width: 0; }
        .ds-form-row select { width: 90px; }
        .ds-add-btn {
          background: #2b8f5a;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 0 12px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
        }
        .ds-add-btn:disabled { opacity: 0.6; }
        .ds-add-btn:hover:not(:disabled) { background: #1e6e45; }
        .ds-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px 4px;
        }
        .ds-list-header h4 {
          margin: 0;
          font-size: 11px;
          text-transform: uppercase;
          color: #666;
          letter-spacing: 0.5px;
        }
        .ds-sync-all {
          background: none;
          border: none;
          color: #2b8f5a;
          font-size: 11px;
          cursor: pointer;
        }
        .ds-sync-all:hover { text-decoration: underline; }
        .ds-list {
          flex: 1;
          overflow-y: auto;
          padding: 0 12px 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .ds-empty {
          padding: 20px;
          text-align: center;
          color: #888;
          font-size: 12px;
        }
        .ds-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: white;
          border: 1px solid #eee;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02);
        }
        .ds-item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
        .ds-item-title { display: flex; align-items: center; gap: 6px; }
        .ds-status { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .ds-status-ready { background: #2b8f5a; }
        .ds-status-pending { background: #aaa; }
        .ds-status-scraping { background: #0078d4; animation: blink 1s infinite; }
        .ds-status-failed { background: #d85a5a; }
        .ds-label {
          font-size: 13px;
          font-weight: 500;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ds-item-meta { font-size: 11px; color: #888; }
        .ds-item-error { font-size: 11px; color: #d85a5a; margin-top: 2px; }
        .ds-item-actions { display: flex; gap: 4px; align-items: flex-start; }
        .ds-item-actions button {
          background: transparent;
          border: 1px solid transparent;
          cursor: pointer;
          font-size: 12px;
          padding: 4px;
          border-radius: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .ds-item-actions button:hover:not(:disabled) { background: #f5f5f5; border-color: #ddd; }
        .ds-item-actions button:disabled { opacity: 0.4; cursor: default; }
      `}</style>
    </div>
  )
}
